import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import { useSnackbar } from 'notistack';
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  Container,
  Link,
  Typography,
} from '@mui/material';
import { Icon } from '@iconify/react';
import cloudApi from '../../app/api/cloudApi';
import { useAvailableActions } from '../../hooks/useAvailableActions';
import { useTranslations } from '../../hooks/use-translations';
import PipelineSideBySideSection from './sections/pipeline-side-by-side-section';

export default function SitePipelineComparePage() {
  const { siteId } = useParams();
  const navigate = useNavigate();
  const auth = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslations();
  const { canReadBoscaAdminReports } = useAvailableActions();
  const [site, setSite] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!siteId || !auth.user?.access_token) {
      return;
    }

    const load = async () => {
      try {
        const response = await cloudApi.getSite(auth.user.access_token, siteId);
        setSite(response.data);
      } catch (e) {
        console.error(e);
        enqueueSnackbar(t('sites.administrative.pipelineCompareLoadSiteFailed'), { variant: 'error' });
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [siteId, auth.user, enqueueSnackbar, t]);

  if (!canReadBoscaAdminReports) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error">{t('sites.administrative.pipelineCompareAccessDenied')}</Alert>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Typography>{t('sites.administrative.pipelineCompareLoading')}</Typography>
      </Container>
    );
  }

  if (!site) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error">{t('sites.administrative.pipelineCompareSiteNotFound')}</Alert>
      </Container>
    );
  }

  return (
    <>
      <Helmet>
        <title>{t('sites.administrative.pipelineComparePageTitle')} | CloudLoq</title>
      </Helmet>

      <Container maxWidth="xl">
        <Box sx={{ mt: 4, mb: 4 }}>
          <Breadcrumbs sx={{ mb: 2 }}>
            <Link component={RouterLink} to="/sites" underline="hover" color="inherit">
              {t('sites.administrative.pipelineCompareBreadcrumbSites')}
            </Link>
            <Link component={RouterLink} to={`/site/${site.id}`} underline="hover" color="inherit">
              {site.name}
            </Link>
            <Typography color="text.primary">
              {t('sites.administrative.pipelineCompareBreadcrumbCurrent')}
            </Typography>
          </Breadcrumbs>

          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Icon icon="mdi:compare" width={32} height={32} />
              <Typography variant="h4">{t('sites.administrative.pipelineComparePageHeading')}</Typography>
            </Box>
            <Button variant="outlined" startIcon={<Icon icon="mdi:arrow-left" />} onClick={() => navigate(`/site/${site.id}`)}>
              {t('sites.administrative.pipelineCompareBackToSite')}
            </Button>
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {site.name} · {site.id}
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('sites.administrative.pipelineSideBySideIntro')}
          </Typography>

          <PipelineSideBySideSection
            accountId={site.accountId}
            siteId={site.id}
            accessToken={auth.user?.access_token}
            compact
          />
        </Box>
      </Container>
    </>
  );
}
